import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ShopifyProduct } from "@/lib/shopify";
import { uniqueDisplayNames } from "@/lib/productTitle";
import { diversifyRail, fetchTypePool } from "@/lib/railDiversity";
import { ProductCard } from "./ProductCard";
import { ProductGridSkeleton } from "./ProductCardSkeleton";

// The affordable, in-stock majority of the catalog — availability is enforced
// inside fetchProducts, and the price filter uses explicit comparisons because
// the `variants.price:0..250` range form silently returns nothing.
const AFFORDABLE_QUERY = "variants.price:<250 AND available_for_sale:true";

// One query for the whole price band comes back almost entirely outdoor rugs,
// so the pool is assembled per product type across the affordable catalog.
const AFFORDABLE_TYPES = [
  "Table Lamps",
  "Throws & Blankets",
  "Accent Throw Pillows",
  "Sculptures",
  "Vases",
  "Mirrors",
  "Wall Decor",
  "Picture Frames",
  "Candles",
  "Bath Rugs",
  "Wall Lighting",
  "Doormats",
];

const COUNT = 12;

export const EverydayFinds = () => {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchTypePool(AFFORDABLE_TYPES, 6, AFFORDABLE_QUERY);
        if (cancelled) return;
        setProducts(diversifyRail(data, { count: COUNT, seed: "everyday-finds" }));

      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);


  const names = useMemo(() => uniqueDisplayNames(products), [products]);

  return (
    <section className="w-[89%] max-w-[1140px] mx-auto pt-4 md:pt-4 pb-4 md:pb-6">
      <div className="flex items-center justify-between gap-3 mb-3 md:mb-4">
        <h2 className="text-[12px] md:text-[13px] tracking-[0.14em] md:tracking-[0.22em] uppercase font-bold text-foreground whitespace-nowrap">
          Everyday Finds Under $250
        </h2>
        <Link
          to="/category/100-250"
          className="text-[10px] tracking-[0.12em] md:tracking-[0.18em] uppercase font-bold text-foreground hover:text-primary inline-flex items-center gap-1.5 whitespace-nowrap shrink-0"
        >
          <span className="hidden sm:inline">Shop everyday finds</span>
          <span className="sm:hidden">View all</span> <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {loading ? (
        <ProductGridSkeleton count={4} />
      ) : products.length === 0 ? (
        <p className="text-[13px] text-muted-foreground py-8 text-center">No products found.</p>
      ) : (
        <div className="stagger-grid grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {products.map((product) => (
            <ProductCard
              key={product.node.id}
              product={product}
              displayName={names[product.node.handle]}
            />
          ))}
        </div>
      )}
    </section>
  );
};
